import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCar,
  faShieldHalved,
  faUsers,
  faChartLine,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import styles from './Login.module.css';

const TEST_ACCOUNTS = [
  { label: 'Admin',             email: 'admin@nula-move.com',            password: 'admin',            role: 'admin' },
];

// eslint-disable-next-line no-unused-vars
const API_TEST_ACCOUNTS = [
  { label: 'Vehicle Owner',     email: 'vehicle_owner@nula-move.com',    password: 'vehicle_owner' },
  { label: 'Private Person',    email: 'private_person@nula-move.com',   password: 'private_person' },
  { label: 'Insurance Agency',  email: 'insurance_agency@nula-move.com', password: 'insurance_agency' },
];

const FEATURES = [
  { icon: faCar,          text: 'Manage vehicle owner applications' },
  { icon: faShieldHalved, text: 'Review insurance paths A & B' },
  { icon: faUsers,        text: 'Monitor all users across roles' },
  { icon: faChartLine,    text: 'Full audit log of every action' },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  function handleRoleSelect(e) {
    const role = e.target.value;
    setSelectedRole(role);
    if (!role) { setEmail(''); setPassword(''); return; }
    const account = TEST_ACCOUNTS.find((a) => a.role === role);
    if (account) { setEmail(account.email); setPassword(account.password); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/applications');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Login failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>

      {/* ── Left branding panel ── */}
      <div className={styles.left}>
        <div className={styles.leftInner}>

          {/* Icon mark */}
          <div className={styles.iconMark}>
            <img src="/admin-new-logo.png" alt="Nula Move" className={styles.iconMarkImg} />
          </div>

          <h1 className={styles.heroTitle}>Nula Move</h1>
          <p className={styles.heroSub}>
            Unified mobility platform — admin control centre
          </p>

          <ul className={styles.featureList}>
            {FEATURES.map((f, i) => (
              <li key={i} className={styles.featureItem}>
                <span className={styles.featureIcon}>
                  <FontAwesomeIcon icon={f.icon} />
                </span>
                {f.text}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right login panel ── */}
      <div className={styles.right}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Sign in</h2>
            <p className={styles.cardSub}>Admin access only</p>
          </div>

          {/* Dev quick-login */}
          <div className={styles.devPanel}>
            <p className={styles.devTitle}>🔧 Dev quick-login</p>
            <select
              className={styles.roleSelect}
              value={selectedRole}
              onChange={handleRoleSelect}
            >
              <option value="">Select a test account…</option>
              {TEST_ACCOUNTS.map((a) => (
                <option key={a.role} value={a.role}>
                  {a.label} — {a.email}
                </option>
              ))}
            </select>
            {selectedRole && (
              <div className={styles.credHint}>
                <span>Email: <strong>{TEST_ACCOUNTS.find(a => a.role === selectedRole)?.email}</strong></span>
                <span>Password: <strong>{TEST_ACCOUNTS.find(a => a.role === selectedRole)?.password}</strong></span>
              </div>
            )}
            <p className={styles.devNote}>
              Other roles (vehicle owner, private person, insurance agency) have their own portals — not built yet. Test those via Postman using the API directly.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>Email address</label>
              <input
                id="email"
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@nula-move.com"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>Password</label>
              <input
                id="password"
                type="password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>
            <button type="submit" className={styles.btn} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in →'}
            </button>

            {error && (
              <div className={styles.errorBox}>
                <strong>Error:</strong> {error}
              </div>
            )}
          </form>

          <p className={styles.checkRow}>
            <FontAwesomeIcon icon={faCircleCheck} className={styles.checkIcon} />
            Secured with JWT authentication
          </p>
        </div>
      </div>
    </div>
  );
}
